import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput,
  fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import { Period, PeriodPicker, periodText, presetRange, usePeriodParams } from './period';
import MonthOverMonth from './analytics/MonthOverMonth';
import {
  EMPTY_TAG_FILTER, LIFECYCLES, LifecyclePill, OriginPill, TagFilter, type TagFilterValue, TagPills, UserTagCells,
  describeTagFilter, matchesTagFilter, tagFilterActive, tagFilterParams, tagText, toLifecycle, useTagOptions,
} from './tags/shared';

/**
 * Analytics group — two dashboards that answer different questions:
 *
 *  · Dashboard      the admin_v3 growth/engagement/retention/outreach page plus
 *                   the shared analytics-exclusion editor from Settings; a
 *                   window or a custom date range.
 *  · Month over month  analytics/monthly: signups, activity and revenue per
 *                   month (analytics/MonthOverMonth.tsx).
 *  · User analytics admin_panel_v2 Tab "User Analytics Dashboard" — nine
 *                   exclusion-filtered tiles, nine cohort drill-downs and the
 *                   new-account trend with its five AND-ed filters.
 *
 * Both read through the same exclusion list, so editing it on one tab changes
 * every number on the other (tabs unmount on switch, so they refetch).
 */

const TABS = ['Dashboard', 'Month over month', 'User analytics', 'Site visitors'];

const AnalyticsPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Dashboard' && <Dashboard />}
      {tab === 'Month over month' && <MonthOverMonth />}
      {tab === 'User analytics' && <UserAnalytics />}
      {tab === 'Site visitors' && <Visitors />}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Site visitors — our own record, alongside GA / Clarity / PostHog
// ---------------------------------------------------------------------------
/**
 * Exists because the vendors disagree with each other and none of them can be
 * joined to our own data. Every row carries their ids, so when the numbers
 * differ this is the fourth measurement that can say which one is wrong.
 */
const VISIT_WINDOWS = ['1', '7', '30', '90', 'custom'];
const VISIT_WINDOW_LABELS: Record<string, string> = {
  '1': 'Today', '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days',
  custom: 'Custom range…',
};

/** Every filter the panel can hold. Flat and all-optional, because it is also
 *  exactly the query string sent to the server — one shape, not a UI model and
 *  a request model that have to be kept in step. */
type VisitFilters = {
  days: string; from: string; to: string; day: string;
  include_bots: boolean; exclude_internal: boolean;
  signed_in: string; bounced: string; bots_only: string;
  q: string;
  device: string; country: string; city: string; network: string; hosting: string;
  path: string; landing_path: string;
  referrer: string; origin: string; utm_source: string; email: string;
  ip: string; visitor_id: string;
};

const EMPTY_FILTERS: VisitFilters = {
  days: '30', from: '', to: '', day: '',
  // Our own machines and our own staff are off by default. The alternative —
  // showing them and expecting everyone to remember — is how a dashboard ends
  // up quietly reporting the team back to itself.
  include_bots: false, exclude_internal: true,
  signed_in: '', bounced: '', bots_only: '', q: '',
  device: '', country: '', city: '', network: '', hosting: '', path: '', landing_path: '',
  referrer: '', origin: '', utm_source: '', email: '', ip: '', visitor_id: '',
};

/** Column filters, in the order they appear in the table. */
const VISIT_FIELDS: { key: keyof VisitFilters; label: string; placeholder: string }[] = [
  { key: 'email', label: 'Who', placeholder: 'email contains…' },
  { key: 'device', label: 'Device', placeholder: 'desktop / mobile / bot' },
  { key: 'landing_path', label: 'Landed on', placeholder: '/pricing' },
  { key: 'path', label: 'Last page', placeholder: '/profile' },
  { key: 'referrer', label: 'Referrer', placeholder: 'https://google.com/' },
  { key: 'origin', label: 'Origin', placeholder: 'https://universitytech.io' },
  { key: 'utm_source', label: 'UTM source', placeholder: 'newsletter' },
  { key: 'country', label: 'Country', placeholder: 'India' },
  { key: 'city', label: 'City', placeholder: 'Gurugram' },
  { key: 'network', label: 'Network', placeholder: 'Jio / Amazon' },
  { key: 'ip', label: 'IP', placeholder: '38.137.' },
  { key: 'visitor_id', label: 'Visitor', placeholder: 'vis_…' },
];

const CHIP_LABELS: Record<string, string> = {
  day: 'Day', signed_in: 'Signed in', bounced: 'Bounced', bots_only: 'Bots only',
  q: 'Search', device: 'Device', country: 'Country', city: 'City', network: 'Network',
  hosting: 'Datacenter', path: 'Last page',
  landing_path: 'Landed on', referrer: 'Referrer', origin: 'Origin',
  utm_source: 'UTM source', email: 'Who', ip: 'IP', visitor_id: 'Visitor',
  from: 'From', to: 'To',
};

/** A site event's props as one compact line — "query: jaggery · length: 7" —
 *  so a visit's step list shows what was searched or clicked, not just that a
 *  click happened. Props arrive as an object (jsonb) or as JSON text. */
const propsText = (v: any): string => {
  let o = v;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch { return o; }
  }
  if (o === null || o === undefined) return '';
  if (typeof o !== 'object') return String(o);
  return Object.entries(o)
    .filter(([, x]) => x !== null && x !== undefined && x !== '')
    .map(([k, x]) => `${k}: ${typeof x === 'object' ? JSON.stringify(x) : String(x)}`)
    .join(' · ');
};

const Visitors: React.FC = () => {
  const [f, setF] = useState<VisitFilters>(EMPTY_FILTERS);
  const [session, setSession] = useState<string | null>(null);
  const [showCols, setShowCols] = useState(false);
  // Lifecycle / tag filter for the Visits table (visitor_tracking.visits reads
  // the standard tag params; the tiles and charts above are not narrowed by it).
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();

  const set = (patch: Partial<VisitFilters>) => setF(prev => ({ ...prev, ...patch }));
  /** Clicking the filter you already have on means "take it off again" — the
   *  same gesture both ways, so nothing becomes a dead end. */
  const toggle = (key: keyof VisitFilters, value: string) =>
    set({ [key]: f[key] === value ? '' : value } as Partial<VisitFilters>);

  // Only what is set travels, so the request is readable and the server's
  // defaults stay the defaults.
  const p: Record<string, string> = { limit: '500' };
  (Object.keys(f) as (keyof VisitFilters)[]).forEach(k => {
    const v = f[k];
    if (typeof v === 'boolean') { if (v) p[k] = '1'; return; }
    if (v) p[k] = v;
  });
  if (f.days === 'custom') delete p.days;      // an explicit range replaces the window
  const dep = JSON.stringify(p);

  // signed_in is this tab's own per-visit filter ("this visit was signed in"),
  // not the account's; the tag filter never sends it here.
  const { signed_in: _accountSignedIn, ...tagParams } = tagFilterParams(tf);
  const tagDep = JSON.stringify(tagParams);
  const overview = useAdminGet<any>('analytics', 'visitors_overview', p, [dep]);
  const list = useAdminGet<any>('analytics', 'visitors_list', { ...p, ...tagParams }, [dep, tagDep]);
  const tagsOn = tagFilterActive(tf);
  const detail = useAdminGet<any>('analytics', 'visitor_session',
    { session_id: session || '' }, [session]);
  // Props flattened to text so the table's row filter can find a query and
  // the CSV carries it; the column is added even if the server's list omits it.
  const sessionEvents: any[] = (detail.data?.events || []).map((e: any) => ({ ...e, props: propsText(e?.props) }));
  const sessionCols: string[] = detail.data?.columns || [];
  const sessionColumns = sessionCols.includes('props') || !sessionEvents.some(e => e.props)
    ? sessionCols : [...sessionCols, 'props'];
  // The values that actually occur, so filtering is picking rather than
  // guessing at spelling. Loaded once — it does not depend on the filters.
  const opts = useAdminGet<any>('analytics', 'visitors_filters', {}, []);
  const OPT_LIST: Partial<Record<keyof VisitFilters, string>> = {
    device: 'devices', country: 'countries', city: 'cities', network: 'networks',
    origin: 'origins', utm_source: 'utm_sources',
  };

  // Geolocation runs beside the visit record, not inside it: the beacon never
  // waits on a lookup, so a fresh visit can sit without a country until the
  // background fill catches up. This shows that fill and can force it.
  const [geoTick, setGeoTick] = useState(0);
  const geo = useAdminGet<any>('analytics', 'visitors_geo_status', {}, [geoTick]);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [geoNote, setGeoNote] = useState('');
  const g = geo.data;
  const count = (v: any) => (v === null || v === undefined) ? '—' : Number(v).toLocaleString();

  // The look-up runs on a server thread and the POST comes straight back, so
  // the panel has to poll the status until the thread says it is done. The
  // timer lives in a ref: React state cannot be read from inside a timer
  // callback, and the unmount cleanup needs the handle regardless of renders.
  // `polling` is the render-visible copy for the button.
  //
  // Budget: keyless, a full run is 500 GETs at a pace plus a TLS handshake
  // each, i.e. several minutes; 150 polls of 2 s covers it. Past that the
  // panel stops watching but says so, rather than pretending the run ended.
  const POLL_MAX = 150;
  const [polling, setPolling] = useState(false);
  const poll = useRef<{ id: ReturnType<typeof setTimeout> | null; n: number } | null>(null);
  const stopPolling = () => {
    if (poll.current?.id) clearTimeout(poll.current.id);
    poll.current = null;
    setPolling(false);
  };
  const startPolling = () => {
    if (poll.current?.id) clearTimeout(poll.current.id);
    poll.current = { id: null, n: 0 };
    setPolling(true);
    setGeoTick(t => t + 1);          // one fetch now; the next is armed when it lands
  };
  // Closing the tab mid-run must not leave a timer bumping state on a
  // component that is gone.
  useEffect(() => () => {
    if (poll.current?.id) clearTimeout(poll.current.id);
    poll.current = null;
  }, []);
  // Only a status fetched AFTER the click can say the run is over — the copy
  // on screen at click time says "not running" because it predates it. So
  // the check hangs off loading flipping back to false, not off the click.
  // Each landed fetch also arms the next one: a timeout here rather than an
  // interval means a slow status query is never superseded by the next tick
  // (useAdminGet drops a superseded answer, and with it the loading flip
  // this effect hangs off), so the count is exact and the loop cannot run
  // forever.
  useEffect(() => {
    if (!poll.current || geo.loading) return;
    if (g?.backfill_running === false) {
      stopPolling();
      setGeoNote('');
      // Visits now carry countries they did not a moment ago, so every view
      // that shows one is stale until it refetches.
      overview.reload(); list.reload(); opts.reload();
      return;
    }
    if (poll.current.n >= POLL_MAX) {
      // No visit row has changed yet — the run resolves every address before
      // it touches one — so there is nothing to reload, only more to wait for.
      stopPolling();
      setGeoNote('Still running on the server — click to keep watching');
      return;
    }
    poll.current.id = setTimeout(() => {
      if (!poll.current) return;
      poll.current.n += 1;
      setGeoTick(t => t + 1);
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.loading]);
  // A run this panel did not start — a beacon's, already going when the tab
  // opened — still has to be watched: nothing else would ever fetch the
  // status again, and the button would sit on a stale "running". This keys
  // on the flag's value, so a run the panel gave up on above is not picked
  // straight back up; the note there says what to do.
  useEffect(() => {
    if (g?.backfill_running && !poll.current) startPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g?.backfill_running]);

  const backfill = async () => {
    setGeoBusy(true); setGeoMsg(null); setGeoNote('');
    const { data, error } = await panelPost('analytics', 'visitors_geo_backfill', { max_ips: 500 });
    setGeoBusy(false);
    if (error || !data) {
      setGeoMsg({ kind: 'error', text: error || 'Could not start the look-up' });
      return;
    }
    // A run that was already going (a beacon started one) is not a failure:
    // it is doing exactly what the click asked for, so just watch it.
    if (data.started === false) setGeoNote('Already running');
    startPolling();
  };

  const k = overview.data?.kpis || {};
  const mmss = (s: number) => `${Math.floor((s || 0) / 60)}m ${Math.round((s || 0) % 60)}s`;

  // The chips are built from the same object the request is, so a filter can
  // never be in force without being visible.
  const chips = (Object.keys(CHIP_LABELS) as (keyof VisitFilters)[])
    .filter(key => typeof f[key] === 'string' && f[key])
    .map(key => ({ key, label: CHIP_LABELS[key as string], value: String(f[key]) }));
  const active = chips.length > 0 || f.include_bots || !f.exclude_internal
    || f.days !== '30' || !!f.from || !!f.to;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-grayscale-1200">
          Our own tracking. Bots are recorded and excluded by default rather than
          dropped, so “how much of this is bots” stays answerable.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-grayscale-1200 flex items-center gap-1.5 cursor-pointer"
            title="Beacons sent from localhost, a LAN address or a preview deploy, plus visits by staff accounts. On by default — otherwise the team shows up as traffic.">
            <input type="checkbox" checked={f.exclude_internal}
              onChange={e => set({ exclude_internal: e.target.checked })} />
            Exclude internal
          </label>
          <label className="text-xs text-grayscale-1200 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={f.include_bots}
              onChange={e => set({ include_bots: e.target.checked })} />
            Include bots
          </label>
          <Select value={f.days}
            // Custom starts as the window it came from, so the request never
            // goes out with no range (the server would fall back to 30 days).
            onChange={v => set(v === 'custom'
              ? { days: v, day: '', ...(f.days === 'custom' ? {} : presetRange(f.days, { '1': 1, '7': 7, '30': 30, '90': 90 })) }
              : { days: v, from: '', to: '', day: '' })}
            options={VISIT_WINDOWS} labels={VISIT_WINDOW_LABELS} />
        </div>
      </div>

      {f.days === 'custom' && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
          <span>From</span>
          <input type="date" value={f.from} onChange={e => set({ from: e.target.value, day: '' })}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <span>to</span>
          <input type="date" value={f.to} onChange={e => set({ to: e.target.value, day: '' })}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
          <span className="text-grayscale-1100">Both ends included. Leave one blank for open-ended.</span>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={f.q} onChange={v => set({ q: v })} wide
          placeholder="Search everything — email, IP, page, referrer, country, network, visitor id…" />
        <Btn small onClick={() => setShowCols(v => !v)}>
          {showCols ? 'Hide field filters' : 'Filter by field'}
        </Btn>
        {(active || tagsOn) && <Btn small onClick={() => { setF(EMPTY_FILTERS); setTf(EMPTY_TAG_FILTER); }}>Reset all</Btn>}
      </div>

      {showCols && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 bg-white rounded-xl
          border border-grayscale-300 p-3">
          {VISIT_FIELDS.map(fd => (
            <label key={fd.key} className="flex flex-col gap-1 text-[11px] text-grayscale-1100">
              {fd.label}
              <input value={String(f[fd.key] || '')} placeholder={fd.placeholder}
                list={OPT_LIST[fd.key] ? `visitopt-${fd.key}` : undefined}
                onChange={e => set({ [fd.key]: e.target.value } as Partial<VisitFilters>)}
                className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
              {OPT_LIST[fd.key] && (
                <datalist id={`visitopt-${fd.key}`}>
                  {((opts.data?.[OPT_LIST[fd.key] as string]) || []).map((v: string) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              )}
            </label>
          ))}
          <label className="flex flex-col gap-1 text-[11px] text-grayscale-1100">
            Signed in
            <select value={f.signed_in} onChange={e => set({ signed_in: e.target.value })}
              className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
              <option value="">Any</option><option value="yes">Signed in</option>
              <option value="no">Anonymous</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-grayscale-1100">
            Engagement
            <select value={f.bounced} onChange={e => set({ bounced: e.target.value })}
              className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
              <option value="">Any</option><option value="yes">Bounced</option>
              <option value="no">Read more than one page</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] text-grayscale-1100">
            Datacenter
            <select value={f.hosting} onChange={e => set({ hosting: e.target.value })}
              className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
              <option value="">Any</option><option value="yes">Datacenter only</option>
              <option value="no">Hide datacenter</option>
            </select>
          </label>
          <div className="sm:col-span-2 lg:col-span-3 flex flex-col gap-1 border-t border-grayscale-200 pt-2">
            <span className="text-[11px] text-grayscale-1100">
              Who, by lifecycle and tags — narrows the Visits table to signed-in visits of matching accounts
            </span>
            <TagFilter value={tf} onChange={setTf} hide={['invite_sent', 'signed_in']} />
          </div>
        </div>
      )}

      {(chips.length > 0 || tagsOn) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {chips.map(c => (
            <Chip key={String(c.key)} label={c.label} value={c.value}
              onClear={() => set({ [c.key]: '' } as Partial<VisitFilters>)} />
          ))}
          {tagsOn && (
            <Chip label="Who (Visits table)" value={describeTagFilter(tf, k => tagOpts.tagByKey[k]?.label || k).join(' · ')}
              onClear={() => setTf(EMPTY_TAG_FILTER)} />
          )}
          <button type="button" onClick={() => setF({ ...EMPTY_FILTERS, days: f.days,
            from: f.from, to: f.to, include_bots: f.include_bots,
            exclude_internal: f.exclude_internal })}
            className="text-xs text-grayscale-1100 underline">clear filters</button>
        </div>
      )}

      {overview.error && <Msg kind="error">{overview.error}</Msg>}
      {overview.loading ? <Loading label="Counting visits…" /> : (
        <KPIRow>
          {/* Cards that describe a subset are the control for that subset.
              Visits and unique visitors describe everything, so they clear. */}
          <KPI label="Visits" value={(k.visits ?? 0).toLocaleString()}
            onClick={() => setF({ ...EMPTY_FILTERS, days: f.days, from: f.from, to: f.to,
              include_bots: f.include_bots, exclude_internal: f.exclude_internal })} />
          <KPI label="Unique visitors" value={(k.visitors ?? 0).toLocaleString()} />
          <KPI label="Signed in" value={(k.signed_in ?? 0).toLocaleString()}
            hint="Visits we could attribute to an account"
            active={f.signed_in === 'yes'} onClick={() => toggle('signed_in', 'yes')} />
          <KPI label="Page views" value={(k.pageviews ?? 0).toLocaleString()} />
          <KPI label="Avg. time" value={mmss(k.avg_seconds)} hint="Only while the tab was visible" />
          <KPI label="Bounced" value={`${k.bounce_pct ?? 0}%`} hint="One page view or fewer"
            active={f.bounced === 'yes'} onClick={() => toggle('bounced', 'yes')} />
          <KPI label="Bot visits" value={(k.bots ?? 0).toLocaleString()}
            hint={f.include_bots ? 'Included in the numbers above' : 'Excluded above unless included'}
            active={f.bots_only === 'yes'}
            onClick={() => f.bots_only === 'yes'
              ? set({ bots_only: '', include_bots: false })
              // Bots are filtered out by default, so asking to see only bots
              // has to switch them back on or the answer is always zero.
              : set({ bots_only: 'yes', include_bots: true })} />
          <KPI label="Internal" value={(k.internal ?? 0).toLocaleString()}
            hint={f.exclude_internal ? 'Hidden — localhost and staff' : 'Localhost / LAN / previews'} />
          <KPI label="Datacenter" value={(k.datacenter ?? 0).toLocaleString()}
            hint="by ASN, heuristic"
            active={f.hosting === 'yes'} onClick={() => toggle('hosting', 'yes')} />
        </KPIRow>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Visits per day">
          {(overview.data?.daily || []).length
            ? <BarList items={(overview.data.daily || []).map((r: any) => ({ label: r.day, value: r.visits }))}
                activeLabel={f.day} onSelect={d => set({ day: f.day === d ? '' : d })} />
            : <div className="text-sm text-grayscale-1100">Nothing in this window yet.</div>}
        </Section>
        <Section title="Where they came from">
          {(overview.data?.top?.referrers || []).length
            ? <BarList items={(overview.data.top.referrers || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.referrer} onSelect={v => toggle('referrer', v)} />
            : <div className="text-sm text-grayscale-1100">No referrers recorded.</div>}
        </Section>
        <Section title="Top pages">
          {(overview.data?.top?.pages || []).length
            ? <BarList items={(overview.data.top.pages || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.path} onSelect={v => toggle('path', v)} />
            : <div className="text-sm text-grayscale-1100">No pages recorded.</div>}
        </Section>
        <Section title="Devices">
          {(overview.data?.top?.devices || []).length
            ? <BarList items={(overview.data.top.devices || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.device} onSelect={v => toggle('device', v)} />
            : <div className="text-sm text-grayscale-1100">No devices recorded.</div>}
        </Section>
        <Section title="Where the beacon came from"
          right={<span className="text-[11px] text-grayscale-1100">the site they were on</span>}>
          {(overview.data?.top?.origins || []).length
            ? <BarList items={(overview.data.top.origins || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.origin} onSelect={v => toggle('origin', v)} />
            : <div className="text-sm text-grayscale-1100">
                No origins recorded yet — only visits since this was added carry one.
              </div>}
        </Section>
        <Section title="Countries">
          {(overview.data?.top?.countries || []).length
            ? <BarList items={(overview.data.top.countries || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.country} onSelect={v => toggle('country', v)} />
            : <div className="text-sm text-grayscale-1100">No countries recorded.</div>}
        </Section>
        <Section title="Cities">
          {(overview.data?.top?.cities || []).length
            ? <BarList items={(overview.data.top.cities || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.city} onSelect={v => toggle('city', v)} />
            : <div className="text-sm text-grayscale-1100">No cities recorded yet.</div>}
        </Section>
        <Section title="Networks"
          right={<span className="text-[11px] text-grayscale-1100">the ISP or cloud the IP belongs to</span>}>
          {(overview.data?.top?.networks || []).length
            ? <BarList items={(overview.data.top.networks || []).map((r: any) => ({ label: r.value, value: r.n }))}
                activeLabel={f.network} onSelect={v => toggle('network', v)} />
            : <div className="text-sm text-grayscale-1100">No networks recorded yet.</div>}
        </Section>
      </div>

      <Section title="Visits"
        right={<span className="text-xs text-grayscale-1100">
          {list.data ? (list.data.truncated
            ? `${(list.data.rows || []).length} of ${list.data.matched.toLocaleString()} matching`
            : `${list.data.matched.toLocaleString()} matching`) : ''}
          {tagsOn ? ' · lifecycle / tag filter on (tiles above not narrowed)' : ''} · click a row for the full journey
        </span>}>
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading ? <Loading /> : (
          <div className="max-h-[30rem] overflow-y-auto border border-primary-200 rounded-xl">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-grayscale-100">
                <tr>{['Started', 'Who', 'Time', 'Pages', 'Device', 'Country', 'Network', 'Landed on', 'Referrer', 'Origin', 'IP']
                  .map(h => <th key={h} className="p-1.5 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(list.data?.rows || []).map((r: any) => (
                  <tr key={r.session_id}
                      onClick={() => setSession(r.session_id)}
                      className="border-b border-primary-100 last:border-0 cursor-pointer hover:bg-primary-50">
                    <td className="p-1.5 whitespace-nowrap">{r.started}</td>
                    <td className="p-1.5">
                      {r.email || <span className="text-grayscale-1100">anonymous</span>}
                      {(r.lifecycle || (Array.isArray(r.tags) && r.tags.length > 0)) && (
                        <div className="mt-0.5"><UserTagCells row={r} max={2} /></div>
                      )}
                    </td>
                    <td className="p-1.5">{mmss(r.duration_s)}</td>
                    <td className="p-1.5">{r.pageviews}</td>
                    <td className="p-1.5">{r.device}</td>
                    <td className="p-1.5 whitespace-nowrap" title={r.as_name || ''}>
                      {r.country || '—'}
                      {r.city && <span className="text-grayscale-1100"> · {r.city}</span>}
                    </td>
                    <td className="p-1.5 max-w-[10rem] truncate" title={r.as_name || ''}>{r.as_name || '—'}</td>
                    <td className="p-1.5">{r.landing_path}</td>
                    <td className="p-1.5 max-w-[16rem] truncate">{r.referrer || '—'}</td>
                    <td className="p-1.5 max-w-[12rem] truncate">{r.origin || '—'}</td>
                    <td className="p-1.5 font-mono">{r.ip}</td>
                  </tr>
                ))}
                {!(list.data?.rows || []).length && (
                  <tr><td colSpan={11} className="p-4 text-center text-grayscale-1100">
                    Nothing matches these filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="IP geolocation"
        right={<Btn small kind="primary" onClick={backfill} disabled={geoBusy || polling}
          title={polling ? 'A look-up is running'
            : 'Resolve up to 500 visit IPs that have no country yet'}>
          {polling ? 'Running…' : geoBusy ? 'Starting…' : 'Look up missing IPs now'}
        </Btn>}>
        {geo.error && <Msg kind="error">{geo.error}</Msg>}
        {g?.error && <Msg kind="error">{g.error}</Msg>}
        {geoMsg && <Msg kind={geoMsg.kind}>{geoMsg.text}</Msg>}
        {geoNote && <div className="text-xs text-grayscale-1100">{geoNote}</div>}
        {/* The status keeps rendering while it re-polls: a spinner every 2 s
            would make the whole section flicker for the length of the run. */}
        {geo.loading && !g ? <Loading /> : g && (
          <>
            <p className="text-xs text-grayscale-1100 -mt-1">
              Country, city and network come from {g.provider}, one lookup per IP, cached,
              and filled in after the visit is recorded — so a brand-new visit can show no
              country for a minute. Datacenter is a guess from the network name.
            </p>
            <div className="text-xs text-grayscale-1100">
              {g.mode === 'keyless'
                ? 'Keyless — IPinfo legacy endpoint, 50,000 lookups/month, no signup. Set IPINFO_TOKEN on the server to switch to IPinfo Lite (unlimited).'
                : 'Token — IPinfo Lite, unlimited lookups.'}
            </div>
            <KPIRow>
              <KPI label="IPs resolved" value={count(g.cached_ok)} hint="Cached with a country" />
              <KPI label="IPs unresolvable" value={count(g.cached_fail)} hint="Private, bogon or unknown" />
              <KPI label="Visit IPs pending" value={count(g.pending_visit_ips)} hint="Visits with no country yet" />
              <KPI label="Unlocated in view" value={(k.unlocated ?? 0).toLocaleString()}
                hint="Visits in the current filter with no country" />
            </KPIRow>
            {g.paused_until && (
              <Msg kind="error">
                Lookups paused until {fmtDate(g.paused_until)}{g.last_error ? ` — ${g.last_error}` : ''}.
              </Msg>
            )}
            {!g.paused_until && g.last_error && (
              <div className="text-xs text-grayscale-1100">Last provider error: {g.last_error}</div>
            )}
            {g.last_backfill && g.last_backfill.at && g.last_backfill.resolved === undefined && (
              <Msg kind="error">
                Last look-up {fmtDate(g.last_backfill.at)} failed: {g.last_backfill.error || 'unknown error'}
              </Msg>
            )}
            {g.last_backfill && g.last_backfill.at && g.last_backfill.resolved !== undefined && (
              <div className="text-xs text-grayscale-1100">
                Last look-up {fmtDate(g.last_backfill.at)}: {count(g.last_backfill.resolved)} resolved,{' '}
                {count(g.last_backfill.failed)} unresolvable, {count(g.last_backfill.updated_rows)} visit
                row(s) updated{g.last_backfill.remaining ? `, ${count(g.last_backfill.remaining)} still pending` : ''}.
              </div>
            )}
          </>
        )}
      </Section>

      {session && (
        <Section title="This visit, step by step"
          right={<Btn small onClick={() => setSession(null)}>Close</Btn>}>
          {detail.loading ? <Loading /> : detail.data && (
            <>
              <div className="text-xs text-grayscale-1100">
                {detail.data.visit?.email || 'anonymous'} · {mmss(detail.data.visit?.duration_s)} ·{' '}
                {detail.data.visit?.device} · {detail.data.visit?.ip}
                {detail.data.visit?.country && <> · {detail.data.visit.country}
                  {detail.data.visit?.city ? `, ${detail.data.visit.city}` : ''}</>}
                {detail.data.visit?.as_name && <> · {detail.data.visit.as_name}</>}
                {detail.data.visit?.origin && <> · from {detail.data.visit.origin}</>}
                {detail.data.visit?.ga_client_id && <> · GA {detail.data.visit.ga_client_id}</>}
                {detail.data.visit?.clarity_id && <> · Clarity {detail.data.visit.clarity_id}</>}
              </div>
              <DataTable rows={sessionEvents}
                columns={sessionColumns} maxHeight="20rem"
                labels={{ props: 'Details' }}
                empty="No events recorded for this visit." />
            </>
          )}
        </Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dashboard — admin_v3 data layer as the single source of truth.
// ---------------------------------------------------------------------------
const WINDOWS = ['7d', '30d', '90d', 'all', 'custom'];
const WINDOW_LABELS: Record<string, string> = {
  '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', all: 'All time', custom: 'Custom range…',
};
const WINDOW_DAYS: Record<string, number | null> = { '7d': 7, '30d': 30, '90d': 90, all: null };

/** A signups bucket by its granularity (UTC): "31 Aug", "w/c 31 Aug 26", "Aug 26". */
const bucketLabel = (r: any): string => {
  const iso = String(r.period || r.bucket || '').slice(0, 10);
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return String(r.bucket);
  const g = r.granularity || 'month';
  if (g === 'day') return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  if (g === 'week') return `w/c ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' })}`;
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const Dashboard: React.FC = () => {
  const [period, setPeriod] = useState<Period>({ preset: '30d', from: '', to: '' });
  const { params: pp } = usePeriodParams(period);
  const ppKey = JSON.stringify(pp);
  // What the numbers below are for: the period in force (an invalid custom
  // range in the picker leaves the last valid one in force).
  const label = periodText({ preset: pp.window || '30d', from: pp.from || '', to: pp.to || '' }, WINDOW_LABELS);
  // Saving the exclusion list changes what every tile below counts, so the
  // save bumps this and each query re-runs. Without it the operator edited the
  // list and kept staring at the pre-save numbers.
  const [exclStamp, setExclStamp] = useState(0);
  const growth = useAdminGet('analytics', 'growth', pp, [ppKey, exclStamp]);
  const signups = useAdminGet('analytics', 'signups_timeseries', pp, [ppKey, exclStamp]);
  const engagement = useAdminGet('analytics', 'engagement', pp, [ppKey, exclStamp]);
  const engagementTs = useAdminGet('analytics', 'engagement_timeseries', pp, [ppKey, exclStamp]);
  const retention = useAdminGet('analytics', 'retention', {}, [exclStamp]);
  const outreach = useAdminGet('analytics', 'outreach', pp, [ppKey, exclStamp]);
  // Members at the end of the period (owner: "cumulative free members end of
  // period — Explorers + trial accounts + Voyager assigned").
  const members = useAdminGet('analytics', 'members_period', pp, [ppKey, exclStamp]);
  const g: any = growth.data || {};
  const split: Record<string, number> = g.new_source_split || g.source_split || {};
  const signupRows: any[] = (signups.data as any[]) || [];
  const sum = (k: string) => signupRows.reduce((a: number, r: any) => a + (Number(r[k]) || 0), 0);

  const monthLabel = (b: any) => {
    const d = new Date(b);
    return isNaN(d.getTime()) ? String(b) : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="text-sm text-grayscale-1200">
          Numbers exclude the team accounts on the exclusion list (bottom of page). Dates are UTC.
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={WINDOWS} labels={WINDOW_LABELS}
          presetDays={WINDOW_DAYS} />
      </div>

      {growth.error && <Msg kind="error">{growth.error}</Msg>}
      {growth.data && (
        <KPIRow>
          <KPI label="Registered (all time)" value={g.total_registered?.toLocaleString?.()}
            hint="every account, however it was made" />
          <KPI label={`Self sign-ups (${label})`} value={g.new_signups?.toLocaleString?.()}
            hint={g.new_accounts !== undefined ? `of ${Number(g.new_accounts).toLocaleString()} accounts made` : undefined} />
          {g.new_admin_added !== undefined && (
            <KPI label={`Added by admin (${label})`} value={Number(g.new_admin_added).toLocaleString()}
              hint="panel, bulk add, People import" />
          )}
          {Object.entries(split).map(([k, v]) => (
            <KPI key={k} label={`via ${k}${g.new_source_split ? '' : ' (all time)'}`} value={Number(v).toLocaleString()}
              hint={g.new_source_split ? 'of the self sign-ups' : undefined} />
          ))}
        </KPIRow>
      )}

      <MembersAtEnd q={members} label={label} />

      <div className="grid md:grid-cols-2 gap-4">
        <Section title={`Self sign-ups over time (${label})`}>
          {signups.error && <Msg kind="error">{signups.error}</Msg>}
          {signups.loading && !signups.data ? <Loading /> : !signupRows.some((r: any) => r.total)
            ? <div className="text-sm text-grayscale-1100">No new accounts in this period.</div>
            : (
              <div className="max-h-80 overflow-y-auto">
                <BarList items={signupRows.map((r: any) => ({
                  label: bucketLabel(r), value: r.self_signups !== undefined ? r.self_signups : r.total }))} />
                <div className="text-[11px] text-grayscale-1100 mt-1">
                  {signupRows[0]?.self_signups !== undefined
                    ? <>Self sign-ups {sum('self_signups').toLocaleString()} · added by admin {sum('admin_added').toLocaleString()}
                      {' '}· {sum('total').toLocaleString()} accounts made in all.</>
                    : <>Every account made (this server does not tell sign-ups from admin adds yet).</>}
                </div>
              </div>
            )}
        </Section>
        <Section title={`Engagement (${label})`}>
          {engagement.error && <Msg kind="error">{engagement.error}</Msg>}
          {engagement.data && (
            <KPIRow>
              <KPI label="Searches" value={engagement.data.searches?.toLocaleString?.()} />
              <KPI label="Techs saved" value={engagement.data.saves} />
              <KPI label="Universities contacted" value={engagement.data.contacts} />
            </KPIRow>
          )}
          {engagementTs.data && (engagementTs.data || []).length > 0 && (
            <DataTable maxHeight="14rem"
              rows={(engagementTs.data || []).map((r: any) => ({
                month: monthLabel(r.bucket), searches: r.searches, saves: r.saves, contacts: r.contacts }))} />
          )}
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Retention (as of today — not affected by the period)">
          {retention.error && <Msg kind="error">{retention.error}</Msg>}
          {retention.loading ? <Loading /> : retention.data && (
            <>
              <KPIRow>
                <KPI label="DAU" value={retention.data.dau} />
                <KPI label="MAU" value={retention.data.mau} />
                <KPI label="Active sessions" value={retention.data.active_sessions} />
                <KPI label="Return rate" value={`${retention.data.return_rate}%`} hint=">1 login" />
              </KPIRow>
              <BarList items={(retention.data.funnel || []).map((f: any) => ({ label: f.label, value: f.count }))}
                total={retention.data.funnel?.[0]?.count} />
            </>
          )}
        </Section>
        <Section title={`University outreach (${label})`}>
          {outreach.error && <Msg kind="error">{outreach.error}</Msg>}
          {outreach.loading ? <Loading /> : outreach.data && (
            <KPIRow>
              <KPI label="Researchers emailed" value={outreach.data.researchers_emailed} />
              <KPI label="Invitations sent" value={outreach.data.invitations_sent} />
              <KPI label="Emails delivered" value={outreach.data.emails_delivered?.toLocaleString?.()} />
              <KPI label="Technologies indexed" value={outreach.data.technologies_indexed?.toLocaleString?.()} hint="today" />
            </KPIRow>
          )}
        </Section>
      </div>

      <Exclusions onSaved={() => setExclStamp(n => n + 1)} />
    </div>
  );
};

/** Members at the end of the period, by lifecycle, with the change since its
 *  start (analytics/members_period). Free = Explorers + trial accounts
 *  (live or lapsed) + Voyager assigned: everyone using an account who is not
 *  paying. Rebuilt from the billing rows for the period's end instant. */
const MembersAtEnd: React.FC<{ q: { data: any; loading: boolean; error: string }; label: string }> = ({ q, label }) => {
  const d = q.data;
  const e = d?.end;
  const st = d?.start;
  const at = d?.end_at ? new Date(d.end_at) : null;
  const atText = at && !isNaN(at.getTime())
    ? new Date(at.getTime() - 1).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) + ' UTC'
    : '';
  const change = (k: string) => (st && e ? Number(e[k] || 0) - Number(st[k] || 0) : null);
  const signed = (n: number | null) => (n === null ? undefined : `${n >= 0 ? '+' : ''}${n.toLocaleString()} over the period`);
  return (
    <Section title={`Members at the end of the period (${label})`}
      right={atText ? <span className="text-[11px] text-grayscale-1100">as at {atText}</span> : undefined}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !d ? <Loading /> : e && (
        <>
          <KPIRow>
            <KPI label="Free members (cumulative)" value={Number(e.free_members).toLocaleString()}
              hint={signed(change('free_members')) || 'Explorers + trial accounts + Voyager assigned'} />
            <KPI label="Explorers" value={Number(e.explorer).toLocaleString()} hint={signed(change('explorer'))} />
            <KPI label="Trial accounts" value={(Number(e.trial) + Number(e.trial_lapsed)).toLocaleString()}
              hint={`${Number(e.trial).toLocaleString()} on a trial · ${Number(e.trial_lapsed).toLocaleString()} lapsed`} />
            <KPI label="Voyager assigned" value={Number(e.assigned).toLocaleString()} hint={signed(change('assigned'))} />
            <KPI label="Voyager paid" value={Number(e.paid).toLocaleString()} hint="not counted as free" />
          </KPIRow>
          <div className="text-[11px] text-grayscale-1100">
            Free members = everyone with an account in use who is not paying, as they stood at the end of the period: Explorers
            (signed in, no plan), trial accounts (on a trial or with a lapsed one) and Voyager assigned (a plan the team gave).
            Accounts never signed in (prospects{e.prospect ? `, ${Number(e.prospect).toLocaleString()} of them` : ''}) are not
            members.{st ? ` At the start of the period there were ${Number(st.free_members).toLocaleString()} free members.` : ''}
          </div>
        </>
      )}
    </Section>
  );
};

/** admin_v3 Settings → analytics exclusion list (shared JSON with the panels). */
const Exclusions: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const list = useAdminGet('analytics', 'exclusions');
  const [text, setText] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const value = text ?? (list.data || []).join('\n');

  const save = async () => {
    const emails = value.split(/\n/).map(l => l.trim()).filter(Boolean);
    const { data, error } = await panelPost('analytics', 'exclusions', { emails });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      // The server lower-cases, strips and de-duplicates before storing, so the
      // saved list can be shorter than what was typed — show what it kept.
      setMsg({ kind: 'ok', text: `Saved ${data?.count ?? emails.length} excluded email(s) — every count on this page has been recomputed.` });
      setText(null);
      list.reload();
      onSaved?.();
    }
  };

  return (
    <Section title="Excluded from analytics (team accounts)"
      right={<Btn small kind="primary" onClick={save}>Save list</Btn>}>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {list.error && <Msg kind="error">{list.error}</Msg>}
      <p className="text-xs text-grayscale-1100 -mt-1">
        One email per line. These accounts are ignored by every count on this page, by the User
        analytics tab and by the legacy panels — same shared list.
      </p>
      {list.loading ? <Loading /> : (
        <textarea value={value} rows={6} onChange={e => setText(e.target.value)}
          className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-mono" />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// User analytics — admin_panel_v2 "User Analytics Dashboard".
// ---------------------------------------------------------------------------
type Metric = { key: string; label: string; description: string; tab: boolean };

/** "Total users" counts everyone with a user_sessions row — and SSO writes
 *  one too, so Google / LinkedIn sign-ins were always in it. The catalogue's
 *  old wording ("with OTP") said otherwise; this is the corrected copy, applied
 *  here as well so the tile is right whichever server answers. */
const SIGNED_IN_COPY = 'Signed in at least once (OTP, Google or LinkedIn)';
const fixMetricCopy = (m: Metric): Metric =>
  m.key === 'total_users' ? { ...m, description: SIGNED_IN_COPY } : m;

const UA_PRESETS = ['all', '30d', '90d', '365d', 'custom'];
const UA_LABELS: Record<string, string> = {
  all: 'All time', '30d': 'Last 30 days', '90d': 'Last 90 days', '365d': 'Last 12 months', custom: 'Custom range…',
};
const UA_DAYS: Record<string, number | null> = { all: null, '30d': 30, '90d': 90, '365d': 365 };

const UserAnalytics: React.FC = () => {
  const catalogue = useAdminGet('analytics', 'user_analytics_metrics');
  // Bumped whenever the exclusion list changes, so every cohort/trend refetches.
  const [stamp, setStamp] = useState(0);
  // "Accounts created" (owner, 2026-10-01: a custom date range): every tile,
  // cohort and the trend narrow to the accounts made in the period; their
  // activity counts stay lifetime. All time = every account, as before.
  const [period, setPeriod] = useState<Period>({ preset: 'all', from: '', to: '' });
  const { params: pp } = usePeriodParams(period);
  const cohort: Record<string, string> = pp.window === 'custom'
    ? Object.fromEntries(Object.entries(pp).filter(([k]) => k === 'from' || k === 'to'))
    : presetRange(pp.window, UA_DAYS);
  const cohortKey = JSON.stringify(cohort);
  // Lifecycle / tag filter on the cohort rows; the lifecycle tiles set it too.
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);

  const metrics: Metric[] = (catalogue.data?.metrics || []).map(fixMetricCopy);
  const filters: { key: string; label: string }[] = (catalogue.data?.trend_filters || []).map(
    (f: { key: string; label: string }) => (/otp/i.test(f.label) ? { ...f, label: 'Signed in (OTP, Google or LinkedIn)' } : f));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="text-sm text-grayscale-1200 max-w-2xl">
          <b>Accounts created:</b> {periodText({ preset: pp.window || 'all', from: pp.from || '', to: pp.to || '' }, UA_LABELS)}.
          {' '}Every number below counts only the accounts made in this period; what they did (sign-ins,
          searches, chats) is counted over their whole history. Dates are UTC.
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={UA_PRESETS} labels={UA_LABELS} presetDays={UA_DAYS} />
      </div>
      {catalogue.error && <Msg kind="error">{catalogue.error}</Msg>}
      {catalogue.loading ? <Loading label="Loading cohort catalogue…" /> : (
        <>
          <UserSummary metrics={metrics} stamp={stamp} onExclusionsChanged={() => setStamp(s => s + 1)}
            cohort={cohort} cohortKey={cohortKey}
            lifecycleFilter={tf.lifecycle}
            onLifecycle={k => setTf(v => ({ ...v, lifecycle: v.lifecycle.length === 1 && v.lifecycle[0] === k ? [] : [k] }))} />
          <UserCohorts metrics={metrics} stamp={stamp} tf={tf} onTf={setTf} cohort={cohort} cohortKey={cohortKey} />
          <UserTrend filters={filters} stamp={stamp} cohort={cohort} cohortKey={cohortKey} />
        </>
      )}
    </div>
  );
};

/** The nine summary tiles, all exclusion-filtered, from one CTE. */
/** by_lifecycle as {key: n} or [{key, n, signed_in_n?}] → per lifecycle key. */
const lifecycleCounts = (v: any): Record<string, { n: number; signed_in_n?: number }> => {
  const out: Record<string, { n: number; signed_in_n?: number }> = {};
  const add = (k: any, n: any, si?: any) => {
    const lk = toLifecycle(k);
    if (!lk) return;
    const cur = out[lk] || { n: 0 };
    cur.n += Number(n || 0);
    if (si !== undefined && si !== null) cur.signed_in_n = (cur.signed_in_n || 0) + Number(si || 0);
    out[lk] = cur;
  };
  if (Array.isArray(v)) v.forEach(r => add(r?.key, r?.n, r?.signed_in_n));
  else if (v && typeof v === 'object') {
    Object.entries(v).forEach(([k, x]: [string, any]) =>
      (x && typeof x === 'object' ? add(k, x.n, x.signed_in_n) : add(k, x)));
  }
  return out;
};

/** The accounts-created range every User analytics call carries ({} = all). */
type Cohort = { cohort: Record<string, string>; cohortKey: string };

const UserSummary: React.FC<{
  metrics: Metric[]; stamp: number; onExclusionsChanged: () => void;
  lifecycleFilter: string[]; onLifecycle: (key: string) => void;
} & Cohort> = ({ metrics, stamp, onExclusionsChanged, lifecycleFilter, onLifecycle, cohort, cohortKey }) => {
  const summary = useAdminGet('analytics', 'user_analytics_summary', cohort, [stamp, cohortKey]);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const s = summary.data;
  const missing: string[] = s?.v2_defaults_not_excluded || [];

  /** Reconcile: the v3 default list dropped three addresses the v2 panel excluded. */
  const addMissing = async () => {
    setBusy(true); setMsg(null);
    const current = await panelGet('analytics', 'exclusions');
    if (current.error) { setMsg({ kind: 'error', text: current.error }); setBusy(false); return; }
    const emails = [...(current.data || []), ...missing];
    const { data, error } = await panelPost('analytics', 'exclusions', { emails });
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: 'ok', text: `Exclusion list now holds ${data?.count ?? emails.length} address(es); every count below has been recomputed.` });
      onExclusionsChanged();
    }
  };

  return (
    <Section title="Summary" right={<Btn small onClick={summary.reload}>Refresh</Btn>}>
      {summary.error && <Msg kind="error">{summary.error}</Msg>}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {summary.loading ? <Loading /> : s && (
        <>
          <KPIRow>
            {metrics.map(m => (
              <KPI key={m.key} label={m.label} value={(s[m.key] ?? 0).toLocaleString()} hint={m.description} />
            ))}
            <KPI label="Total Searches" value={(s.total_searches ?? 0).toLocaleString()}
              hint="Cosmos searches run by the accounts above" />
            <KPI label="Total Chats" value={(s.total_chats ?? 0).toLocaleString()}
              hint="AI-chat conversations — the older feature, not search" />
          </KPIRow>
          {s.by_lifecycle && (() => {
            const by = lifecycleCounts(s.by_lifecycle);
            return (
              <div className="flex flex-col gap-1.5">
                <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">
                  Lifecycle — every account on this tab, click to filter the cohort table
                </div>
                <div className="flex gap-2 flex-wrap">
                  {LIFECYCLES.map(l => {
                    const c = by[l.key] || { n: 0 };
                    const on = lifecycleFilter.length === 1 && lifecycleFilter[0] === l.key;
                    return (
                      <button key={l.key} type="button" onClick={() => onLifecycle(l.key)}
                        title={on ? 'Filtering the cohort table by this — click to clear' : l.hint}
                        className={`text-left rounded-xl border px-3 py-2 min-w-[140px] flex-1 flex flex-col items-start gap-0.5 hover:border-primary-400 ${
                          on ? 'border-primary-500 ring-1 ring-primary-500' : 'border-grayscale-300 bg-white'}`}>
                        <LifecyclePill value={l.key} />
                        <span className="text-lg font-bold text-grayscale-1300 font-grotesk">{c.n.toLocaleString()}</span>
                        {c.signed_in_n !== undefined && (
                          <span className="text-[11px] text-grayscale-1100">{c.signed_in_n.toLocaleString()} signed in</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {Array.isArray(s.by_origin) && s.by_origin.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap text-xs text-grayscale-1200">
              <span className="text-[11px] uppercase tracking-wide text-grayscale-1100">Account origin</span>
              {s.by_origin.map((o: any) => (
                <span key={o.key} className="inline-flex items-center gap-1.5">
                  <OriginPill value={o.key} label={o.label} />
                  <b>{Number(o.n || 0).toLocaleString()}</b>
                  <span className="text-grayscale-1100">({Number(o.signed_in_n || 0).toLocaleString()} signed in)</span>
                </span>
              ))}
              <span className="text-[11px] text-grayscale-1100">filter the cohort table by it with “Account origin” below</span>
            </div>
          )}
          <p className="text-xs text-grayscale-1100">
            {s.excluded_count} address(es) on the exclusion list are left out of every tile, cohort
            and trend on this tab. “100% Personal Profile” and “100% R&amp;D Profile” read 0 because
            no profile can currently reach full marks — the job title is blank on every profile row,
            and personas / R&amp;D goals / problem statement are empty. The Streamlit panel showed
            the same zeros: it is a profile-form gap, not a reporting fault.
          </p>
          {missing.length > 0 && (
            <div className="text-sm rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200 flex flex-wrap items-center gap-2">
              <span>
                The legacy panel also excluded {missing.join(', ')} — the current shared list does
                not, so these tiles count those accounts.
              </span>
              <Btn small disabled={busy} onClick={addMissing}>
                {busy ? 'Saving…' : 'Add them to the exclusion list'}
              </Btn>
            </div>
          )}
        </>
      )}
    </Section>
  );
};

// Profile completion is scored in three weighted groups that total 100
// (profile_spec.GROUPS: personal 40, professional 30, context 30). The
// professional group was missing from this table entirely, so there was no
// overall number and two of the three groups were invisible.
// Searches sit right after logins: they are the activity signal now. "Chats"
// is the older AI-chat feature, which most active users never touched — a
// Voyager member with 44 searches showed 0 there and read as dormant.
const ROW_COLS = ['email', 'lifecycle', 'tag_list', 'full_name', 'company', 'country', 'created_date', 'login_count',
  'search_count', 'last_search', 'chat_count', 'has_password', 'has_clerk_profile',
  'overall_profile_pct', 'personal_profile_pct', 'professional_profile_pct', 'rd_profile_pct'];
const ROW_LABELS: Record<string, string> = {
  lifecycle: 'Lifecycle', tag_list: 'Tags', full_name: 'Name', company: 'Company', created_date: 'Created (UTC)', login_count: 'Logins',
  search_count: 'Searches', last_search: 'Last search (UTC)',
  chat_count: 'Chats (AI chat)', has_password: 'Password', has_clerk_profile: 'Clerk',
  overall_profile_pct: 'Profile overall', personal_profile_pct: 'Personal',
  professional_profile_pct: 'Professional', rd_profile_pct: 'R&D / context',
};
const pct = (v: any) => `${v ?? 0}%`;
const ROW_RENDER = {
  // created_date is already a UTC wall-clock string; letting fmtCell re-parse it
  // would shift every timestamp by the operator's local offset.
  created_date: (r: Record<string, any>) => r.created_date || '—',
  last_search: (r: Record<string, any>) => r.last_search || '—',
  overall_profile_pct: (r: Record<string, any>) => pct(r.overall_profile_pct),
  personal_profile_pct: (r: Record<string, any>) => pct(r.personal_profile_pct),
  professional_profile_pct: (r: Record<string, any>) => pct(r.professional_profile_pct),
  rd_profile_pct: (r: Record<string, any>) => pct(r.rd_profile_pct),
  lifecycle: (r: Record<string, any>) => <LifecyclePill value={r.lifecycle} subtag={r.subtag} label={r.lifecycle_label} />,
  tag_list: (r: Record<string, any>) => <TagPills tags={r.tags} max={3} nowrap />,
};

/** One cohort at a time, drilled down to the user rows behind the tile. */
const UserCohorts: React.FC<{
  metrics: Metric[]; stamp: number; tf: TagFilterValue; onTf: (v: TagFilterValue) => void;
} & Cohort> = ({ metrics, stamp, tf, onTf, cohort, cohortKey }) => {
  const [metric, setMetric] = useState('total_accounts');
  const [search, setSearch] = useState('');
  const rows = useAdminGet('analytics', 'user_analytics_rows',
    { metric, search, limit: 5000, ...cohort }, [metric, search, stamp, cohortKey]);

  const labels = metrics.map(m => m.label);
  const active = metrics.find(m => m.key === metric);
  const d = rows.data;
  // Filtered here, on the rows the server returned (decorated with lifecycle
  // + tags); tag_list is the text form, so row search and the CSV carry it.
  const tagsOn = tagFilterActive(tf);
  const shown = useMemo(() => (d?.rows || [])
    .map((r: any) => ({ ...r, tag_list: tagText(r) }))
    .filter((r: any) => !tagsOn || matchesTagFilter(r, tf)), [d, tf, tagsOn]);

  return (
    <Section title="Cohort drill-down"
      right={<Btn small onClick={rows.reload}>Refresh</Btn>}>
      <SubTabs tabs={labels} active={active?.label || ''}
        onChange={l => { setMetric(metrics.find(m => m.label === l)?.key || 'total_accounts'); setSearch(''); }} />
      <div className="flex items-center gap-2 flex-wrap">
        <TextInput value={search} onChange={setSearch} placeholder="Search email / name / company…" wide />
        <span className="text-xs text-grayscale-1100">
          {d ? (tagsOn ? `${shown.length.toLocaleString()} of ${d.count.toLocaleString()} user(s)`
            : `${d.count.toLocaleString()} user(s)`) : '—'}
          {active?.description ? ` · ${active.description}` : ''}
        </span>
      </div>
      {/* Filtered in the browser (matchesTagFilter): the rows carry no dates
          or profile %, so those filters are not offered here. */}
      <TagFilter value={tf} onChange={v => onTf(v)} hide={['invite_sent', 'dates', 'profile']} counts={false} />
      {rows.error && <Msg kind="error">{rows.error}</Msg>}
      {d?.truncated && (
        <Msg kind="error">
          Showing the newest {d.limit.toLocaleString()} accounts only — this cohort is larger.
          Narrow it with the search box before exporting.
        </Msg>
      )}
      {rows.loading ? <Loading /> : (
        <DataTable rows={shown} columns={ROW_COLS} labels={ROW_LABELS} render={ROW_RENDER}
          csvName={`user_analytics_${metric}`} maxHeight="32rem"
          empty={tagsOn ? 'No account in this cohort matches the lifecycle / tag filter.' : 'No accounts match this cohort.'} />
      )}
    </Section>
  );
};

const GRANULARITIES = ['monthly', 'daily'];
const GRANULARITY_LABELS: Record<string, string> = { monthly: 'By month', daily: 'By day' };
/** Bars for the most recent N periods; the table underneath keeps every period. */
const CHART_PERIODS: Record<string, number> = { month: 24, day: 30 };

const periodLabel = (p: string, gran: string) => {
  const d = new Date(gran === 'day' ? `${p}T00:00:00Z` : `${p}-01T00:00:00Z`);
  if (isNaN(d.getTime())) return p;
  return gran === 'day'
    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' })
    : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

/** New accounts per period with the five AND-ed cohort filters. */
const UserTrend: React.FC<{ filters: { key: string; label: string }[]; stamp: number } & Cohort> =
  ({ filters, stamp, cohort, cohortKey }) => {
  const [granularity, setGranularity] = useState('monthly');
  const [on, setOn] = useState<Record<string, boolean>>({});
  const activeKeys = filters.filter(f => on[f.key]).map(f => f.key);
  const trend = useAdminGet('analytics', 'user_analytics_trend',
    { granularity, ...Object.fromEntries(activeKeys.map(k => [k, '1'])), ...cohort },
    [granularity, activeKeys.join(','), stamp, cohortKey]);

  const d = trend.data;
  const gran: string = d?.granularity || 'month';
  const rows: { period: string; new_users: number }[] = d?.rows || [];
  const shown = rows.slice(-(CHART_PERIODS[gran] || 24));

  return (
    <Section title="New accounts over time"
      right={<Select value={granularity} onChange={setGranularity}
        options={GRANULARITIES} labels={GRANULARITY_LABELS} />}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-grayscale-1100 uppercase tracking-wide">Only count accounts that are</span>
        {filters.map(f => (
          <Btn key={f.key} small kind={on[f.key] ? 'primary' : 'subtle'}
            onClick={() => setOn(s => ({ ...s, [f.key]: !s[f.key] }))}>
            {f.label}
          </Btn>
        ))}
        {activeKeys.length > 0 && <Btn small onClick={() => setOn({})}>Clear filters</Btn>}
      </div>
      {trend.error && <Msg kind="error">{trend.error}</Msg>}
      {trend.loading ? <Loading /> : d && (
        <>
          <KPIRow>
            <KPI label="New accounts" value={(d.total ?? 0).toLocaleString()}
              hint={(d.active_filters || []).length ? `Filtered: ${(d.active_filters || []).join(' + ')}` : 'No filters — every account'} />
            <KPI label={gran === 'day' ? 'Days with signups' : 'Months with signups'} value={d.periods ?? 0}
              hint="Periods with no signups are omitted" />
            <KPI label="Busiest period"
              value={rows.length ? periodLabel(rows.reduce((a, b) => (b.new_users >= a.new_users ? b : a)).period, gran) : '—'}
              hint={rows.length ? `${Math.max(...rows.map(r => r.new_users))} accounts` : undefined} />
          </KPIRow>
          {!rows.length ? (
            <div className="text-sm text-grayscale-1100 py-4 text-center">
              No accounts match this combination of filters.
            </div>
          ) : (
            <>
              <BarList items={shown.map(r => ({ label: periodLabel(r.period, gran), value: r.new_users }))}
                total={shown.reduce((a, b) => a + b.new_users, 0)} />
              {shown.length < rows.length && (
                <p className="text-xs text-grayscale-1100">
                  Showing the most recent {shown.length} of {rows.length} periods — the table below
                  and the CSV hold all of them.
                </p>
              )}
              <DataTable maxHeight="18rem" csvName={`new_users_by_${gran}`}
                rows={rows.slice().reverse().map(r => ({
                  period: r.period, label: periodLabel(r.period, gran), new_users: r.new_users }))}
                labels={{ period: gran === 'day' ? 'Day' : 'Month', label: 'Period', new_users: 'New accounts' }} />
            </>
          )}
        </>
      )}
    </Section>
  );
};

export default AnalyticsPanel;
